import { useCreateCrawlRequestMutationApi } from '@aibees/elephant/api';
import { CrawlRequestGrouping } from '@aibees/elephant/types';
import {
  cleanLinkedInUrl,
  isValidLinkedInCompanyUrl,
  isValidLinkedInContactUrl,
} from '@aibees/elephant/utils/linkedin-validators';
import { OutlinedInput } from '@aibees/shared/components';
import { useUnMount } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Typography } from '@mui/material';
import { ChangeEventHandler, memo, useMemo, useState } from 'react';
import { useCrawlRequestErrorContext } from './crawl-request-error-context';
import * as Styled from './styles';
import * as SX from './sx';
import type { CrawlRequestProps as Props } from './types';

export const CrawlRequest = memo(
  ({ className, onSubmit, searchCategory, serviceType }: Props) => {
    const [value, setValue] = useState<string>('');
    const { error, touched, setError, setTouched } =
      useCrawlRequestErrorContext();
    const createCrawlRequestApi = useCreateCrawlRequestMutationApi();

    useUnMount(() => {
      setError(false);
      setTouched(false);
    });

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      if (e.target.value.length) setValue(e.target.value);
      else {
        setValue('');
        setTouched(false);
      }
    };

    const placeholder = useMemo(() => {
      return searchCategory === 'company'
        ? 'Use LinkedIn Company URL to start data request.'
        : 'Use LinkedIn Profile URL to start Data Request';
    }, [searchCategory]);

    const titleText = useMemo(() => {
      return searchCategory === 'company'
        ? 'Company Not Listed?'
        : 'People Not Listed?';
    }, [searchCategory]);

    const errorText = useMemo(() => {
      return searchCategory === 'company'
        ? 'Please Enter a Valid LinkedIn Company URL.'
        : 'Please Enter a Valid LinkedIn Profile URL.';
    }, [searchCategory]);

    const handleRequest = () => {
      const cleanedValue = cleanLinkedInUrl(value);
      setTouched(true);

      const isValid =
        searchCategory === 'company'
          ? isValidLinkedInCompanyUrl(value)
          : isValidLinkedInContactUrl(value);

      setError(!isValid);
      createCrawlRequestApi
        .mutateAsync({
          url: cleanedValue,
          grouping:
            searchCategory === 'company'
              ? CrawlRequestGrouping.COMPANY
              : CrawlRequestGrouping.PERSON,
          metadata: {
            type: serviceType,
          },
        })
        .then(() => {
          setValue('');
          setTouched(false);
          onSubmit?.();
        });
    };

    return (
      <Styled.Wrapper
        className={classnames('CrawlRequest', className)}
        sx={SX.root}
      >
        <Typography variant="body3" fontWeight={700} color="grey.700">
          {titleText}
        </Typography>
        <Box
          display="flex"
          alignItems="flex-start"
          gap={1}
          flexDirection="column"
          width="100%"
        >
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <OutlinedInput
              fullWidth
              size="small"
              startIcon="linkedin-contain"
              placeholder={placeholder}
              onChange={handleChange}
              value={value}
              error={error && touched}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              endIcon={error && touched ? 'info' : undefined}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!value || createCrawlRequestApi.isLoading}
              onClick={(e) => {
                e.stopPropagation();
                handleRequest();
              }}
            >
              Send Request
            </Button>
          </Box>

          {error && touched && (
            <Typography sx={SX.errorText}>{errorText}</Typography>
          )}
        </Box>
      </Styled.Wrapper>
    );
  }
);

CrawlRequest.displayName = 'CrawlRequest';
